import { customFetch } from '../../services/apiService';
import React, { useState, useEffect } from 'react';
import { 
  Key, ShieldCheck, Eye, EyeOff, Save, Lock, 
  Sparkles, Loader2, CheckCircle2, AlertCircle, Database, Server,
  CreditCard, DollarSign, Cpu, Plus, Trash2, ArrowUp, ArrowDown, RefreshCw
} from 'lucide-react';
import CustomDialog from '../CustomDialog';

export const AppConfig: React.FC = () => {
  const [secretEncryptionKey, setSecretEncryptionKey] = useState('');
  const [elevenLabsApiKey, setElevenLabsApiKey] = useState('');
  const [systemGeminiApiKey, setSystemGeminiApiKey] = useState('');
  const [geminiActiveModels, setGeminiActiveModels] = useState<string[]>([
    'gemini-2.5-flash',
    'gemini-1.5-flash',
    'gemini-1.5-pro',
    'gemini-2.0-flash'
  ]);
  const [newModelInput, setNewModelInput] = useState('');
  const [isMonetizationEnabled, setIsMonetizationEnabled] = useState<boolean>(true);

  // Password Visibility toggles
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showElevenLabsKey, setShowElevenLabsKey] = useState(false);
  const [showGeminiKey, setShowGeminiKey] = useState(false);

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  // Dialog State
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    type?: 'success' | 'error' | 'warning' | 'confirm';
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
      const res = await customFetch('/api/config', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const cfg = data.config || data.data || {};
        setSecretEncryptionKey(cfg.secretEncryptionKey || '');
        setElevenLabsApiKey(cfg.elevenLabsApiKey || '');
        setSystemGeminiApiKey(cfg.systemGeminiApiKey || '');
        
        if (Array.isArray(cfg.geminiActiveModels) && cfg.geminiActiveModels.length > 0) {
          setGeminiActiveModels(cfg.geminiActiveModels);
        } else if (cfg.geminiActiveModel) {
          setGeminiActiveModels([cfg.geminiActiveModel]);
        }
        
        if (cfg.isMonetizationEnabled !== undefined) {
          setIsMonetizationEnabled(Boolean(cfg.isMonetizationEnabled));
        }
        if (cfg.updatedAt) setUpdatedAt(cfg.updatedAt);
      }
    } catch (err) {
      console.error("Lỗi tải Cấu hình Ứng dụng:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleAddModel = (modelName: string) => {
    const trimmed = modelName.trim();
    if (!trimmed) return;
    if (geminiActiveModels.includes(trimmed)) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Model đã tồn tại',
        message: `Model "${trimmed}" đã có trong danh sách kích hoạt.`
      });
      return;
    }
    setGeminiActiveModels([...geminiActiveModels, trimmed]);
    setNewModelInput('');
  };

  const handleRemoveModel = (index: number) => {
    if (geminiActiveModels.length <= 1) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Yêu cầu tối thiểu',
        message: 'Hệ thống cần ít nhất 1 Model Gemini trong danh sách để hoạt động.'
      });
      return;
    }
    setGeminiActiveModels(geminiActiveModels.filter((_, i) => i !== index));
  };

  const handleMoveModel = (index: number, direction: 'up' | 'down') => {
    const newIdx = direction === 'up' ? index - 1 : index + 1;
    if (newIdx < 0 || newIdx >= geminiActiveModels.length) return;
    const updated = [...geminiActiveModels];
    const temp = updated[index];
    updated[index] = updated[newIdx];
    updated[newIdx] = temp;
    setGeminiActiveModels(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!secretEncryptionKey.trim()) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Thiếu thông tin bắt buộc',
        message: 'Secret Encryption Key là thông số bắt buộc để hệ thống mã hóa dữ liệu bảo mật.'
      });
      return;
    }

    if (geminiActiveModels.length === 0) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Thiếu cấu hình Model',
        message: 'Vui lòng thêm ít nhất 1 Model Gemini vào danh sách kích hoạt.'
      });
      return;
    }

    setIsSaving(true);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
      const primaryModel = geminiActiveModels[0] || 'gemini-2.5-flash';

      const res = await customFetch('/api/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          secretEncryptionKey: secretEncryptionKey.trim(),
          elevenLabsApiKey: elevenLabsApiKey.trim(),
          systemGeminiApiKey: systemGeminiApiKey.trim(),
          geminiActiveModels: geminiActiveModels,
          geminiActiveModel: primaryModel,
          isMonetizationEnabled: Boolean(isMonetizationEnabled)
        })
      });

      if (res.ok) {
        const data = await res.json();
        const cfg = data.config || {};
        if (Array.isArray(cfg.geminiActiveModels) && cfg.geminiActiveModels.length > 0) {
          setGeminiActiveModels(cfg.geminiActiveModels);
        }
        if (cfg.isMonetizationEnabled !== undefined) {
          setIsMonetizationEnabled(Boolean(cfg.isMonetizationEnabled));
        }
        setUpdatedAt(cfg.updatedAt || new Date().toISOString());

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('app_config_updated', {
            detail: { 
              isMonetizationEnabled: Boolean(isMonetizationEnabled),
              geminiActiveModels: geminiActiveModels,
              geminiActiveModel: primaryModel
            }
          }));
        }

        setDialog({
          isOpen: true,
          type: 'success',
          title: 'Lưu Cấu hình Thành công',
          message: data.message || 'Danh sách Model Gemini dự phòng và khóa bảo mật đã được lưu thành công!'
        });
      } else {
        const errData = await res.json();
        setDialog({
          isOpen: true,
          type: 'error',
          title: 'Lỗi Lưu Cấu hình',
          message: errData.message || 'Không thể lưu Cấu hình Ứng dụng.'
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Lỗi Kết Nối',
        message: 'Đã xảy ra lỗi khi gửi yêu cầu tới máy chủ.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Info Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-purple-950 to-slate-900 text-white p-6 rounded-3xl shadow-lg border border-purple-900/40 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-purple-500/10 blur-3xl pointer-events-none" />

        <div className="relative z-10 space-y-2">
          <div className="flex items-center space-x-2 text-purple-300">
            <Lock className="w-5 h-5 text-purple-400" />
            <span className="text-xs font-bold uppercase tracking-wider">Bảo Mật Server AES & API Keys</span>
          </div>
          <h2 className="text-xl font-black tracking-tight">Cấu hình Ứng dụng & Khóa Mã hóa</h2>
          <p className="text-xs text-purple-200/80 max-w-2xl leading-relaxed">
            Các API Key và Khóa bí mật được lưu trữ tập trung trên hệ thống VPS MongoDB. Dữ liệu nhạy cảm được mã hóa cấp mã nguồn AES-256 trước khi khởi tạo dịch vụ AI Trợ lý.
          </p>

          {updatedAt && (
            <div className="text-[11px] text-purple-300/80 pt-1 font-mono">
              Cập nhật gần nhất: {new Date(updatedAt).toLocaleString('vi-VN')}
            </div>
          )}
        </div>
      </div>

      {/* Main Configuration Form */}
      <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-2xs space-y-6">
        {loading ? (
          <div className="py-12 text-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-600" />
            <span>Đang nạp dữ liệu cấu hình hệ thống...</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 1. Secret Encryption Key */}
            <div className="space-y-2 pb-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-extrabold text-slate-900 flex items-center space-x-1.5">
                  <Key className="w-4 h-4 text-purple-600" />
                  <span>Secret Encryption Key (Bắt buộc)</span>
                </label>
                <span className="text-[10px] font-bold px-2 py-0.5 bg-rose-50 text-rose-700 rounded-md border border-rose-100">
                  Required AES Key
                </span>
              </div>
              <p className="text-xs text-slate-500">
                Khóa dùng mã hóa 2 chiều chuỗi dữ liệu SSO Token và mã hóa Gemini API Key riêng tư của Giáo viên.
              </p>

              <div className="relative">
                <input
                  type={showSecretKey ? 'text' : 'password'}
                  value={secretEncryptionKey}
                  onChange={e => setSecretEncryptionKey(e.target.value)}
                  placeholder="Nhập Secret Encryption Key bí mật..."
                  required
                  className="w-full bg-slate-50 border border-slate-300 rounded-2xl py-3 pl-4 pr-12 text-xs font-mono font-bold text-slate-900 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowSecretKey(!showSecretKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600 rounded-lg"
                >
                  {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* 2. ElevenLabs API Key */}
            <div className="space-y-2 pb-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-extrabold text-slate-900 flex items-center space-x-1.5">
                  <Server className="w-4 h-4 text-indigo-600" />
                  <span>ElevenLabs API Key (TTS Voice Generation)</span>
                </label>
                <span className="text-[10px] font-bold px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-md border border-indigo-100">
                  Thuyết minh Giọng nói
                </span>
              </div>
              <p className="text-xs text-slate-500">
                Sử dụng cho tính năng AI Đọc thuyết minh Giáo án, đọc diễn cảm thơ và tạo Podcast bài giảng.
              </p>

              <div className="relative">
                <input
                  type={showElevenLabsKey ? 'text' : 'password'}
                  value={elevenLabsApiKey}
                  onChange={e => setElevenLabsApiKey(e.target.value)}
                  placeholder="sk_11labs_..."
                  className="w-full bg-slate-50 border border-slate-300 rounded-2xl py-3 pl-4 pr-12 text-xs font-mono text-slate-900 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowElevenLabsKey(!showElevenLabsKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600 rounded-lg"
                >
                  {showElevenLabsKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* 3. System Gemini API Key */}
            <div className="space-y-2 pb-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-extrabold text-slate-900 flex items-center space-x-1.5">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <span>System Gemini API Key (Dự phòng Hệ thống)</span>
                </label>
                <span className="text-[10px] font-bold px-2 py-0.5 bg-amber-50 text-amber-800 rounded-md border border-amber-200">
                  Fallback AI Model
                </span>
              </div>
              <p className="text-xs text-slate-500">
                Khóa Gemini mặc định cấp hệ thống khi tài khoản người dùng dùng thử chưa tự thiết lập API Key cá nhân.
              </p>

              <div className="relative">
                <input
                  type={showGeminiKey ? 'text' : 'password'}
                  value={systemGeminiApiKey}
                  onChange={e => setSystemGeminiApiKey(e.target.value)}
                  placeholder="AIzaSy..."
                  className="w-full bg-slate-50 border border-slate-300 rounded-2xl py-3 pl-4 pr-12 text-xs font-mono text-slate-900 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowGeminiKey(!showGeminiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600 rounded-lg"
                >
                  {showGeminiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* 4. Danh sách Model Gemini Kích hoạt & Dự phòng */}
            <div className="space-y-4 pb-5 border-b border-slate-100">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <label className="block text-xs font-extrabold text-slate-900 flex items-center space-x-1.5">
                    <Cpu className="w-4 h-4 text-emerald-600" />
                    <span>Danh sách Model Gemini kích hoạt & Dự phòng</span>
                  </label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Danh sách các model AI cho phép sử dụng. Model đầu tiên sẽ là <strong>Mặc định</strong>, các model tiếp theo dùng làm <strong>Dự phòng khi Google bị quá tải 503</strong>.
                  </p>
                </div>
                <span className="text-[10px] font-bold px-2.5 py-0.5 bg-emerald-50 text-emerald-800 rounded-md border border-emerald-200">
                  {geminiActiveModels.length} Model Đang Kích Hoạt
                </span>
              </div>

              {/* Danh sách Active Models với Thứ tự Ưu tiên */}
              <div className="space-y-2">
                {geminiActiveModels.map((modelName, idx) => (
                  <div 
                    key={modelName + idx}
                    className={`flex items-center justify-between p-3 rounded-2xl border transition-all ${
                      idx === 0 
                        ? 'bg-purple-50/70 border-purple-200 shadow-2xs' 
                        : 'bg-slate-50/80 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2.5 min-w-0">
                      <span className={`w-6 h-6 rounded-lg text-xs font-bold flex items-center justify-center shrink-0 ${
                        idx === 0 
                          ? 'bg-purple-600 text-white shadow-2xs' 
                          : 'bg-slate-200 text-slate-700'
                      }`}>
                        {idx + 1}
                      </span>
                      <div className="truncate">
                        <span className="text-xs font-mono font-bold text-slate-900 truncate block">
                          {modelName}
                        </span>
                        <span className="text-[10px] text-slate-500 font-medium">
                          {idx === 0 ? '★ Mặc định ưu tiên cao nhất' : `Dự phòng cấp ${idx}`}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-1 shrink-0">
                      {/* Move Up */}
                      <button
                        type="button"
                        disabled={idx === 0}
                        onClick={() => handleMoveModel(idx, 'up')}
                        title="Tăng độ ưu tiên"
                        className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 rounded-lg hover:bg-slate-200/60 transition-colors"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>

                      {/* Move Down */}
                      <button
                        type="button"
                        disabled={idx === geminiActiveModels.length - 1}
                        onClick={() => handleMoveModel(idx, 'down')}
                        title="Giảm độ ưu tiên"
                        className="p-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 rounded-lg hover:bg-slate-200/60 transition-colors"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>

                      {/* Remove */}
                      <button
                        type="button"
                        onClick={() => handleRemoveModel(idx)}
                        title="Xóa khỏi danh sách"
                        className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Thêm model tùy chỉnh */}
              <div className="flex items-center space-x-2 pt-1">
                <input
                  type="text"
                  value={newModelInput}
                  onChange={e => setNewModelInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddModel(newModelInput);
                    }
                  }}
                  placeholder="Nhập tên model (VD: gemini-2.5-pro, gemini-1.5-pro-latest...)"
                  className="flex-1 bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-3.5 text-xs font-mono font-medium text-slate-900 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => handleAddModel(newModelInput)}
                  className="px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold flex items-center space-x-1 transition-all shrink-0 cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Thêm Model</span>
                </button>
              </div>

              {/* Quick Preset Badges */}
              <div className="flex items-center flex-wrap gap-1.5 pt-1">
                <span className="text-[11px] text-slate-400 font-medium mr-1">Thêm nhanh gợi ý:</span>
                {[
                  { id: 'gemini-2.5-flash', label: '+ gemini-2.5-flash' },
                  { id: 'gemini-1.5-flash', label: '+ gemini-1.5-flash' },
                  { id: 'gemini-1.5-pro', label: '+ gemini-1.5-pro' },
                  { id: 'gemini-2.0-flash', label: '+ gemini-2.0-flash' },
                  { id: 'gemini-2.5-pro', label: '+ gemini-2.5-pro' }
                ].map(preset => {
                  const isAdded = geminiActiveModels.includes(preset.id);
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      disabled={isAdded}
                      onClick={() => handleAddModel(preset.id)}
                      className={`text-[11px] font-mono px-2.5 py-1 rounded-lg border transition-all ${
                        isAdded
                          ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
                          : 'bg-purple-50/70 text-purple-700 border-purple-200 hover:bg-purple-100 hover:text-purple-900 cursor-pointer'
                      }`}
                    >
                      {preset.label} {isAdded && '✓'}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 5. Cấu hình Hệ thống Thanh toán (Monetization) */}
            <div className="bg-gradient-to-br from-amber-50/60 via-slate-50 to-emerald-50/40 rounded-2xl p-5 border border-slate-200 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center space-x-2.5">
                  <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-700 flex items-center justify-center font-bold">
                    <CreditCard className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider">
                      Cấu hình Hệ thống Thanh toán (Monetization)
                    </h3>
                    <p className="text-[11px] text-slate-500 font-medium">
                      Quản lý cơ chế thu phí, nạp Xu và các gói thành viên VIP/SVIP toàn sàn
                    </p>
                  </div>
                </div>

                <span className={`text-[10px] font-black px-2.5 py-1 rounded-full border ${
                  isMonetizationEnabled 
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-300' 
                    : 'bg-slate-200 text-slate-700 border-slate-300'
                }`}>
                  {isMonetizationEnabled ? 'ĐANG BẬT' : 'ĐANG TẮT'}
                </span>
              </div>

              <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-slate-200/80 shadow-2xs">
                <div className="space-y-1 pr-4">
                  <label
                    htmlFor="toggle-monetization"
                    className="text-xs font-extrabold text-slate-900 cursor-pointer block"
                  >
                    Kích hoạt thu phí toàn hệ thống (Nạp Xu, Mua VIP...)
                  </label>
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    Bật hoặc tắt toàn bộ tính năng thương mại hóa, cổng nạp tiền/Xu và các gói tài khoản VIP/SVIP trên nền tảng.
                  </p>
                </div>

                {/* Toggle Switch */}
                <button
                  id="toggle-monetization"
                  type="button"
                  role="switch"
                  aria-checked={isMonetizationEnabled}
                  onClick={() => setIsMonetizationEnabled(!isMonetizationEnabled)}
                  className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#236539]/30 ${
                    isMonetizationEnabled ? 'bg-[#236539]' : 'bg-slate-300'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                      isMonetizationEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4 border-t border-slate-100 flex justify-end">
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-3 bg-[#224783] hover:bg-[#1b3a6b] text-white font-extrabold text-xs rounded-2xl shadow-md transition-all active:scale-98 flex items-center space-x-2 disabled:opacity-50 shrink-0 cursor-pointer"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span>Lưu Cấu hình</span>
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Custom Dialog */}
      <CustomDialog
        isOpen={dialog.isOpen}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        onClose={() => setDialog(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default AppConfig;
